import PropTypes from 'prop-types'
import React, { Component, Fragment } from 'react'
import { withRouter } from '@/checkout-instore-legacy/utils/withRouter'
import { FormattedMessage } from 'react-intl'
import sortBy from 'lodash/sortBy'

import Loader from '@/checkout-instore-legacy/components/Commons/Loader'
import FooterButton from '@/checkout-instore-legacy/components/Commons/Elements/FooterButton'
import * as CheckoutFetcher from '@/checkout-instore-legacy/fetchers/CheckoutFetcher'
import * as CustomerFetcher from '@/checkout-instore-legacy/fetchers/CustomerFetcher'
import {
  genericCriticalError,
  orderPlacedStatusViewEvent,
  pageviewOrderPlacedEvent,
} from '@/checkout-instore-legacy/utils/EventHelper'
import * as WebViewBridge from '@/checkout-instore-legacy/utils/WebViewBridge'
import {
  goToHome,
  goToExternalOrderPlaced,
} from '@/checkout-instore-legacy/utils/routesUtils'
import {
  normalizeOrderId,
  areOrderIdsEquals,
  hydrateOrderGroup,
} from '@/checkout-instore-legacy/order/utils'
import { getInstoreConfig } from '@/checkout-instore-legacy/utils/configUtils'
import * as ErrorUtils from '@/checkout-instore-legacy/utils/ErrorUtils'

import { OrderPlacedHeader } from './OrderPlacedHeader'
import OrderPlacedDetail from './OrderPlacedDetail'
import { getOrderGroupState } from '@/checkout-instore-legacy/order/orderPlacedUtil'
import { OrderPlacedState } from '@/checkout-instore-legacy/order/typings'
import { ORDER_PLACED_AUTO_REFRESH_SECONDS } from '@/checkout-instore-legacy/order/constants'
import { newOrder } from '@/checkout-instore-legacy/orders/actions'
import { getReduxStore } from '@/checkout-instore-legacy/redux/reduxStore'
import { scrollToTop } from '@/checkout-instore-legacy/utils/WindowUtils'

class OrderPlaced extends Component {
  constructor(props) {
    super(props)

    const { query } = this.props.location

    const printImmediately = query.printImmediately !== 'false'
    const orders = props.orderGroup

    this.state = {
      orders,
      orderGroupId: normalizeOrderId(query.orderId),
      orderPlacedUrl: getInstoreConfig().orderPlacedUrl,
      printImmediately,
      ordersState: '',
      hasIncompleteOrders: false,
      autoRefresh: {
        status: 'none',
        remainingTime: null,
      },
      refreshInterval: null,
      finishedOrder: !this.props.router.location.search, // if it's coming from Last Orders menu option is unfinished
    }
  }

  startNewOrder() {
    WebViewBridge.sendEvent({ type: 'newOrder' })
    this.props.clearOrderGroup()

    getReduxStore().dispatch(newOrder())
  }

  changeOrdersState(newState) {
    this.setState({ ordersState: newState })
  }

  handleIncompleteOrder(orderGroupState) {
    if (
      orderGroupState === OrderPlacedState.IncompleteFinished ||
      orderGroupState === OrderPlacedState.IncompleteUnfinished
    ) {
      this.setState({ hasIncompleteOrders: true })

      if (this.state.finishedOrder) {
        this.setState({
          autoRefresh: {
            status: 'active',
            remainingTime: ORDER_PLACED_AUTO_REFRESH_SECONDS,
          },
          refreshInterval: setInterval(this.handleAutoRefresh.bind(this), 1000),
        })
      }
    } else {
      this.setState({ hasIncompleteOrders: false })
    }
  }

  handleAutoRefresh() {
    if (this.state.autoRefresh.remainingTime <= 0) {
      clearInterval(this.state.refreshInterval)

      this.setState({
        autoRefresh: {
          status: 'updating',
          remainingTime: null,
        },
      })

      const orderGroupId = this.state.orders[0].orderGroup

      this.recoverNewOrderGroup(orderGroupId)
        .then((newOrders) => {
          this.setState({
            orders: newOrders,
            autoRefresh: { status: 'none' },
          })
          this.changeOrdersStateBasedOnNewOrderGroup(newOrders)
        })
        .catch(() => {
          this.setState({ autoRefresh: { status: 'none' } })
        })
    } else {
      this.setState({
        autoRefresh: {
          status: 'active',
          remainingTime: this.state.autoRefresh.remainingTime - 1,
        },
      })
    }
  }

  changeOrdersStateBasedOnNewOrderGroup(orderGroup) {
    const orderGroupState = getOrderGroupState(
      orderGroup,
      this.state.finishedOrder
    )

    orderPlacedStatusViewEvent(
      orderGroupState,
      orderGroup,
      this.state.orderGroupId
    )

    if (!orderGroupState) {
      return
    }

    this.handleIncompleteOrder(orderGroupState)

    this.changeOrdersState(orderGroupState)
  }

  async recoverNewOrderGroup(orderGroupId) {
    let newOrders = null

    try {
      newOrders = await CheckoutFetcher.getOrderGroup(orderGroupId)
    } catch (error) {
      genericCriticalError('load-orderplaced-without-order', {
        context: 'Sale',
        message: ErrorUtils.getErrorMessage(error),
      })
      return null
    }

    try {
      const order = newOrders[0]
      const profile = await CustomerFetcher.clientProfile(
        order.clientProfileData
      )
      const hydratedOrderGroup = hydrateOrderGroup(
        newOrders,
        { orderFormId: order.orderFormId },
        profile
      )

      return hydratedOrderGroup
    } catch (error) {
      genericCriticalError('load-orderplaced-without-complete-profile', {
        context: 'Sale',
        message: ErrorUtils.getErrorMessage(error),
      })
    }

    return newOrders
  }

  startNewOrderAndGoToHome = () => {
    this.startNewOrder()
    goToHome(this.props.router)
  }

  handlePopStateEvent = (event) => {
    if (event.state !== null) {
      this.startNewOrderAndGoToHome()
    }
  }

  async componentDidMount() {
    const { orders, orderGroupId } = this.state

    WebViewBridge.sendEvent({ type: 'orderPlaced' })

    const order = orders && orders.length > 0 ? orders[0] : null

    pageviewOrderPlacedEvent(orders, orderGroupId)

    if (orderGroupId) {
      if (!order || !areOrderIdsEquals(order.orderId, orderGroupId)) {
        this.props.clearOrderGroup()
        this.setState({
          orders: [],
        })
        await this.recoverNewOrderGroup(orderGroupId).then((newOrders) => {
          if (!newOrders) {
            this.props.router.push('/payment')
          }

          this.setState({
            orders: newOrders,
          })
        })
      }
    } else if (!orders || orders.length === 0) {
      genericCriticalError('load-orderplaced-without-order', {
        context: 'Internal',
      })
      this.props.router.push('/payment')
    }

    this.changeOrdersStateBasedOnNewOrderGroup(this.state.orders)

    if (this.state.orderPlacedUrl) {
      this.startNewOrder()
      goToExternalOrderPlaced(this.props.router, order && order.orderGroup)
    }

    if (this.props.location.state?.redirectToHome) {
      window.addEventListener('popstate', this.handlePopStateEvent)
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    // listen if the cancellation process is complete and then retrieve the orders
    if (nextProps.cancelOrderDetails?.refreshOrders) {
      scrollToTop()
      this.setState({
        autoRefresh: {
          status: 'updating',
          remainingTime: 0,
        },
        refreshInterval: setInterval(this.handleAutoRefresh.bind(this), 1000),
      })
      this.props.clearCancelGivenOrder()
    }
  }

  componentWillUnmount() {
    clearInterval(this.state.refreshInterval)
    window.removeEventListener('popstate', this.handlePopStateEvent)
  }

  handleClickNewOrder = () => {
    this.startNewOrderAndGoToHome()
  }

  render() {
    const { orderPlacedUrl, printImmediately } = this.state

    const orders = sortBy(this.state.orders, [(order) => order.orderId])

    const { device } = this.props

    const loading = !orders || orders.length === 0
    const numberOfOrders = loading ? 0 : orders.length
    const { printingConfig, printedOrderExtraText } = getInstoreConfig()

    return (
      <div className="flex flex-column vh-100 bg-light-silver">
        <div className="flex-auto overflow-auto">
          <div className="w-60-l center mv7-l pb6-l">
            {orderPlacedUrl || loading ? (
              <Loader loading />
            ) : (
              <Fragment>
                <OrderPlacedHeader
                  orders={orders}
                  router={this.props.router}
                  onNewOrderAction={this.handleClickNewOrder}
                  ordersState={this.state.ordersState}
                  printImmediately={
                    printImmediately &&
                    printingConfig &&
                    printingConfig.printPageAutomatically
                  }
                  actionsDisabled={this.state.hasIncompleteOrders}
                  autoRefresh={this.state.autoRefresh}
                />
                {numberOfOrders > 0 &&
                  orders.map((order, i) => (
                    <OrderPlacedDetail
                      key={order.orderId}
                      order={order}
                      router={this.props.router}
                      printImmediately={printImmediately}
                      showBorder={i < numberOfOrders - 1}
                      device={device}
                      actionsDisabled={this.state.hasIncompleteOrders}
                    />
                  ))}
                <div className="dn show-print mt8 mh5 bb b--black" />
                <div className="dn show-print mh5 mt4 tc">
                  {printedOrderExtraText}
                </div>
              </Fragment>
            )}
          </div>
        </div>
        <div className="flex-none no-print dn-l bg-base">
          {orderPlacedUrl ? null : (
            <FooterButton
              id="new-order"
              kind="primary"
              onClick={this.handleClickNewOrder}
            >
              <FormattedMessage id="orderPlacedStartNewSale" />
            </FooterButton>
          )}
        </div>
      </div>
    )
  }
}

OrderPlaced.propTypes = {
  location: PropTypes.any,
  router: PropTypes.any,
  orderGroup: PropTypes.arrayOf(PropTypes.object),
  clearOrderGroup: PropTypes.func,
  device: PropTypes.shape({
    deviceId: PropTypes.string,
    deviceName: PropTypes.string,
  }),
}

export default withRouter(OrderPlaced)
